// Ghana — Oware Abapa engine.
//
// Board: 2 rows × 6 pits. Each pit starts with 4 seeds. 48 seeds in total.
//   row 0 = Player B (top), row 1 = Player A (bottom).
// Sowing is counter-clockwise around BOTH rows, starting from the pit
// immediately following the selected pit.
//   Route (CCW from any starting pit): A0,A1,A2,A3,A4,A5, B5,B4,B3,B2,B1,B0, A0, …
//
// Rules implemented:
//   - 12-counter skip: if a single sowing lap contains ≥12 seeds, skip the
//     original starting pit when sowing reaches it again.
//   - Capture: only on opponent's row. After the final seed is sown, if it
//     left an opponent pit at exactly 2 or 3, capture it; continue walking
//     backwards through preceding opponent pits and capture every consecutive
//     pit that contains exactly 2 or 3. Stop at any other number, an empty pit,
//     or the player's own row.
//   - Feeding rule: when the opponent's row is empty, the active player must
//     play a move that feeds at least one seed into the opponent's row when
//     such a move exists.
//   - Grand-slam cancellation: a move that would capture every seed from the
//     opponent's row is played (sowing happens) but the capture is cancelled
//     so the game can continue.
//   - End: ≥25 captured wins; 24-24 is a draw; if a player has no legal move,
//     the remaining seeds on the board are awarded to the player on whose row
//     they sit, then totals are compared.

export const RULES_VERSION = "2026.02.18";
export const RULESET_ID = "gh-oware-abapa";
export const ROWS = 2;
export const COLS = 6;
export const PLAYER_A = "A";
export const PLAYER_B = "B";

// CCW route starting from A0 (bottom-left), traversing A0..A5, B5..B0, back to A0.
const ROUTE = [
  [1, 0], [1, 1], [1, 2], [1, 3], [1, 4], [1, 5],
  [0, 5], [0, 4], [0, 3], [0, 2], [0, 1], [0, 0],
];

function routeIndex(r, c) {
  for (let i = 0; i < ROUTE.length; i++) if (ROUTE[i][0] === r && ROUTE[i][1] === c) return i;
  return -1;
}

function ownsRow(player, row) {
  if (player === PLAYER_A) return row === 1;
  return row === 0;
}

function cloneBoard(b) { return b.map((row) => row.slice()); }
function emptyBoard() { return Array.from({ length: ROWS }, () => Array(COLS).fill(0)); }

export function createInitialState() {
  const board = emptyBoard();
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) board[r][c] = 4;
  return {
    rulesetId: RULESET_ID,
    rulesVersion: RULES_VERSION,
    board,
    currentPlayer: PLAYER_A,
    phase: "NORMAL",
    captures: { A: 0, B: 0 },
    status: "ACTIVE",
    winner: null,
    endReason: null,
    turnNumber: 1,
    moveNumber: 0,
    history: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function countersOnRowOf(board, player) {
  const row = player === PLAYER_A ? 1 : 0;
  let s = 0;
  for (let c = 0; c < COLS; c++) s += board[row][c];
  return s;
}

function ownNonEmptyPits(board, player) {
  const row = player === PLAYER_A ? 1 : 0;
  const out = [];
  for (let c = 0; c < COLS; c++) if (board[row][c] > 0) out.push([row, c]);
  return out;
}

// Simulate sowing from a starting pit; returns { board, landing, sownCount, originalCount }
function simulateSowing(board, startR, startC) {
  const b = cloneBoard(board);
  const count = b[startR][startC];
  b[startR][startC] = 0;
  const startIdx = routeIndex(startR, startC);
  let idx = startIdx;
  let remaining = count;
  let last = [startR, startC];
  while (remaining > 0) {
    idx = (idx + 1) % ROUTE.length;
    // Skip start pit on long laps (≥12 seeds total).
    if (count >= 12 && idx === startIdx) continue;
    const [nr, nc] = ROUTE[idx];
    b[nr][nc] += 1;
    last = [nr, nc];
    remaining -= 1;
  }
  return { board: b, landing: last, sownCount: count };
}

// Compute the capture chain from a landing (without mutating). Returns captured pits in order.
function computeCaptureChain(board, player, landing) {
  const [lr, lc] = landing;
  // Capture only when final seed is on opponent's row.
  if (ownsRow(player, lr)) return [];
  const idxLand = routeIndex(lr, lc);
  const captures = [];
  for (let k = 0; k < ROUTE.length; k++) {
    const i = (idxLand - k + ROUTE.length) % ROUTE.length;
    const [r, c] = ROUTE[i];
    // Stop if we've walked back to our own row.
    if (ownsRow(player, r)) break;
    const v = board[r][c];
    if (v === 2 || v === 3) {
      captures.push([r, c]);
    } else {
      break;
    }
  }
  return captures;
}

function hasLegalMoveSimple(board, player) {
  return ownNonEmptyPits(board, player).length > 0;
}

// Feeding-aware legal move generator.
export function getLegalMoves(state) {
  const { board, currentPlayer } = state;
  const opp = currentPlayer === PLAYER_A ? PLAYER_B : PLAYER_A;
  const allOwn = ownNonEmptyPits(board, currentPlayer);
  if (allOwn.length === 0) return [];
  const oppHasSeeds = countersOnRowOf(board, opp) > 0;
  if (oppHasSeeds) return allOwn;
  // Opponent row empty → must feed if any move feeds.
  const feeding = allOwn.filter(([r, c]) => {
    const { board: post } = simulateSowing(board, r, c);
    return countersOnRowOf(post, opp) > 0;
  });
  return feeding.length > 0 ? feeding : allOwn;
}

export function isLegalMove(state, r, c) {
  return getLegalMoves(state).some(([rr, cc]) => rr === r && cc === c);
}

function awardLeftoverSeeds(board) {
  // When the game ends because a player has no legal move, remaining seeds on
  // each row are awarded to the player on whose side they sit.
  const A = countersOnRowOf(board, PLAYER_A);
  const B = countersOnRowOf(board, PLAYER_B);
  return { A, B };
}

export function applyMove(state, r, c) {
  if (state.status !== "ACTIVE") throw new Error("GAME_NOT_ACTIVE");
  if (!ownsRow(state.currentPlayer, r)) throw new Error("NOT_OWN_PIT");
  if (!isLegalMove(state, r, c)) throw new Error("ILLEGAL_MOVE");

  const player = state.currentPlayer;
  const opp = player === PLAYER_A ? PLAYER_B : PLAYER_A;
  const events = [];
  events.push({ type: "TURN_STARTED", player, turn: state.turnNumber });
  events.push({ type: "PIT_SELECTED", from: [r, c], count: state.board[r][c] });

  const { board: postBoard, landing, sownCount } = simulateSowing(state.board, r, c);
  events.push({ type: "COUNTERS_PICKED_UP", from: [r, c], count: sownCount });
  events.push({ type: "SOWING_COMPLETE", landing, sown: sownCount });

  // Compute captures.
  const captureChain = computeCaptureChain(postBoard, player, landing);
  let captureCount = 0;
  let board = postBoard;

  // Grand-slam check: would these captures clear the opponent's entire row?
  const oppRow = opp === PLAYER_A ? 1 : 0;
  let oppRowAfter = 0;
  for (let cc = 0; cc < COLS; cc++) oppRowAfter += board[oppRow][cc];
  const captureTotal = captureChain.reduce((s, [pr, pc]) => s + board[pr][pc], 0);
  const wouldClearOpponent = captureChain.length > 0 && captureTotal === oppRowAfter;

  if (captureChain.length > 0 && wouldClearOpponent) {
    events.push({ type: "GRAND_SLAM_CANCELLED", pits: captureChain });
  } else if (captureChain.length > 0) {
    board = cloneBoard(board);
    events.push({ type: "CAPTURE_TRIGGERED", chain: captureChain });
    for (const [pr, pc] of captureChain) {
      const n = board[pr][pc];
      board[pr][pc] = 0;
      captureCount += n;
      events.push({ type: "COUNTERS_CAPTURED", from: [pr, pc], count: n });
    }
  }

  const captures = { A: state.captures.A, B: state.captures.B };
  captures[player] += captureCount;

  // End-condition checks.
  let status = "ACTIVE";
  let winner = null;
  let endReason = null;

  if (captures[player] >= 25) {
    status = "WON";
    winner = player;
    endReason = "TWENTY_FIVE_CAPTURED";
    events.push({ type: "GAME_WON", player, reason: endReason });
  } else if (captures.A === 24 && captures.B === 24) {
    status = "DRAWN";
    endReason = "DRAW_24_24";
    events.push({ type: "GAME_DRAWN", reason: endReason });
  }

  // Switch player and check legal moves for opponent.
  const probe = { ...state, board, currentPlayer: opp, captures };
  const nextLegal = getLegalMoves(probe);
  if (status === "ACTIVE" && nextLegal.length === 0) {
    // Opponent cannot move (and cannot be fed). Award remaining seeds.
    const leftover = awardLeftoverSeeds(board);
    captures.A += leftover.A;
    captures.B += leftover.B;
    // Zero the board.
    board = emptyBoard();
    events.push({ type: "REMAINING_SEEDS_AWARDED", A: leftover.A, B: leftover.B });
    if (captures.A === captures.B) {
      status = "DRAWN";
      endReason = "DRAW_BY_NO_MOVE";
      events.push({ type: "GAME_DRAWN", reason: endReason });
    } else {
      status = "WON";
      winner = captures.A > captures.B ? PLAYER_A : PLAYER_B;
      endReason = "OPPONENT_NO_LEGAL_MOVE";
      events.push({ type: "GAME_WON", player: winner, reason: endReason });
    }
  }

  events.push({
    type: "TURN_ENDED",
    player,
    landingPit: landing,
    captured: captureCount,
    grandSlamCancelled: wouldClearOpponent && captureChain.length > 0,
  });

  const historyEntry = {
    player,
    from: [r, c],
    landingPit: landing,
    captured: captureCount,
    capturedPits: captureChain,
    relays: 0,
    grandSlamCancelled: wouldClearOpponent && captureChain.length > 0,
    prevBoard: state.board,
    prevCaptures: state.captures,
    prevPhase: state.phase,
    prevCurrentPlayer: state.currentPlayer,
    prevStatus: state.status,
    prevWinner: state.winner,
    prevEndReason: state.endReason,
    prevTurnNumber: state.turnNumber,
    events,
  };

  return {
    state: {
      ...state,
      board,
      currentPlayer: status === "ACTIVE" ? opp : state.currentPlayer,
      captures,
      status,
      winner,
      endReason,
      turnNumber: status === "ACTIVE" ? state.turnNumber + 1 : state.turnNumber,
      moveNumber: state.moveNumber + 1,
      history: [...state.history, historyEntry],
      updatedAt: Date.now(),
    },
    events,
  };
}

export function undoMove(state) {
  if (!state.history || state.history.length === 0) return state;
  const last = state.history[state.history.length - 1];
  return {
    ...state,
    board: last.prevBoard,
    currentPlayer: last.prevCurrentPlayer,
    phase: last.prevPhase,
    captures: last.prevCaptures,
    status: last.prevStatus,
    winner: last.prevWinner,
    endReason: last.prevEndReason,
    turnNumber: last.prevTurnNumber,
    moveNumber: Math.max(0, state.moveNumber - 1),
    history: state.history.slice(0, -1),
    updatedAt: Date.now(),
  };
}

export function totalCounters(state) {
  let s = state.captures.A + state.captures.B;
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) s += state.board[r][c];
  return s;
}

export function describeMove(entry) {
  if (!entry) return "";
  const p = entry.player === PLAYER_A ? "Player 1" : "Player 2";
  const parts = [`${p} sowed ${entry.events.find((e) => e.type === "COUNTERS_PICKED_UP")?.count ?? 0} seeds counter-clockwise.`];
  if (entry.grandSlamCancelled) {
    parts.push("The move would have captured every opponent seed — grand-slam capture cancelled.");
  } else if (entry.captured > 0) {
    parts.push(`Captured ${entry.captured} seed${entry.captured === 1 ? "" : "s"} from ${entry.capturedPits.length} pit${entry.capturedPits.length === 1 ? "" : "s"}.`);
  } else {
    parts.push("No capture.");
  }
  return parts.join(" ");
}
